import Typography from '@mui/material/Typography';
import { DateCell } from '@openx/components-administration/lib/UsersList/DateCell.tsx';
import { ExpirationDateCell } from '@openx/components-organization/lib/components/OrganizationDetails/components/ExpirationDateCell.tsx';
import { useConfirmationModal } from '@openx/components-organization/lib/components/OrganizationDetails/hooks/useConfirmationModal.tsx';
import type { OrganizationApiKeyData } from '@openx/types/organization.ts';
import type { Columns, RowActionProps } from '@openx/types/tableHelpers.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useGetApiKeysTableConfig = () => {
  const { t } = useTranslation();
  const handleDeleteApiKey = useConfirmationModal();

  const activeKeystableColumns: Columns<OrganizationApiKeyData> = useMemo(
    () => [
      {
        title: t('Name'),
        key: 'first_name',
        width: '60%',
        style: {
          paddingTop: '13px',
          paddingBottom: '13px',
        },
        render: ({ first_name }) => (
          <Typography variant="body1Bold" component="span">
            {first_name}
          </Typography>
        ),
      },
      {
        title: t('Expire in'),
        key: 'expires_at',
        width: '20%',
        render: ({ expires_at }) => <DateCell date={expires_at} />,
      },
      {
        title: t('Created'),
        key: 'created_date',
        width: '20%',
        isDate: true,
        isExactDate: true,
      },
    ],
    [t]
  );

  const expiredKeysTableColumns: Columns<OrganizationApiKeyData> = useMemo(
    () => [
      {
        title: t('Name'),
        key: 'first_name',
        width: '60%',
        style: {
          paddingTop: '13px',
          paddingBottom: '13px',
        },
        render: ({ expires_at, first_name }) => (
          <ExpirationDateCell expirationDate={expires_at}>
            <Typography variant="body1Bold" component="span">
              {first_name}
            </Typography>
          </ExpirationDateCell>
        ),
      },
      {
        title: t('Expired'),
        key: 'expires_at',
        width: '20%',
        isDate: true,
        isExactDate: true,
      },
      {
        title: t('Created'),
        key: 'created_date',
        width: '20%',
        isDate: true,
        isExactDate: true,
      },
    ],
    [t]
  );

  const rowAction: RowActionProps<OrganizationApiKeyData> = useMemo(
    () => ({
      label: 'delete',
      'data-test': 'delete-api-key',
      onClick: ({ id, first_name }) => handleDeleteApiKey(id, first_name),
    }),
    [handleDeleteApiKey]
  );

  return { expiredKeysTableColumns, activeKeystableColumns, rowAction };
};
